import { useTranslate } from 'module/hooks/use-translation';
import { Option, Select, selectComponents } from '@rocketlane/ui';
import { FC, useCallback, useMemo } from 'react';
import { GroupBase, OptionProps } from 'react-select';
import { fieldTypeAsOptions } from './utils';
import { getFieldIcon } from '../fields/field-types';
import { UiFieldType } from '../fields/types';

interface FieldTypeOption extends Option {
  value: UiFieldType;
}

export const CustomOption = (
  props: OptionProps<FieldTypeOption, false, GroupBase<FieldTypeOption>>,
) => {
  const { data } = props;

  return (
    // The feature selector belongs to this host; the row/state remain package-owned.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <selectComponents.Option {...props}>
      <span data-cy={`settings_field_type_${data.value}`}>
        {props.children}
      </span>
    </selectComponents.Option>
  );
};
interface Props {
  value?: UiFieldType;
  onChange?: (next: UiFieldType) => void;
  isDisabled?: boolean;
}
export const FieldTypeSelect: FC<Props> = ({ value, onChange, isDisabled }) => {
  const translate = useTranslate();
  const fieldTypeOptions = useMemo<FieldTypeOption[]>(
    () =>
      fieldTypeAsOptions(translate).map((option) => ({
        ...option,
        icon: getFieldIcon(option.value),
      })),
    [translate],
  );
  const selectedOption = useMemo(
    () =>
      value == null
        ? undefined
        : fieldTypeOptions.find((option) => option.value === value),
    [fieldTypeOptions, value],
  );
  const onTypeChange = useCallback(
    (option: FieldTypeOption | null) => {
      if (option) {
        onChange?.(option.value);
      }
    },
    [onChange],
  );

  return (
    <Select<FieldTypeOption>
      isClearable={false}
      isDisabled={isDisabled}
      menuPortalTarget={null}
      options={fieldTypeOptions}
      value={selectedOption}
      onChange={onTypeChange}
      components={{ Option: CustomOption }}
    />
  );
};
